import { useEffect, useRef, useState } from "react";
import { Ban, MailWarning, UserCheck, ChevronDown } from "lucide-react";
import { TONE_SOFT } from "../../../shared/tones";

export type PrimeContactStatus = "active" | "do_not_contact" | "bounced";

const STATUS_META: Record<PrimeContactStatus, { label: string; icon: typeof Ban; classes: string }> = {
  active: { label: "Active", icon: UserCheck, classes: TONE_SOFT.success },
  do_not_contact: { label: "Do Not Contact", icon: Ban, classes: TONE_SOFT.danger },
  bounced: { label: "Bounced", icon: MailWarning, classes: TONE_SOFT.warning },
};

const STATUS_ORDER: PrimeContactStatus[] = ["active", "do_not_contact", "bounced"];

interface Props {
  status: PrimeContactStatus;
  onSelect: (target: PrimeContactStatus) => void;
  /** Compact icon-only trigger for tight spaces like a table row — the label still shows inside the open menu. */
  compact?: boolean;
}

/** Shows a Prime's current status as a colored pill/icon; clicking it opens a
 * menu to switch to one of the other two statuses. Confirmation (reason,
 * overwrite warning) happens in SetPrimeContactStatusModal, opened by the caller's
 * onSelect — this component only picks the target, never writes anything itself. */
export default function PrimeContactStatusDropdown({ status, onSelect, compact }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  const meta = STATUS_META[status];
  const Icon = meta.icon;

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`Status: ${meta.label} — click to change`}
        className={`inline-flex items-center gap-1.5 rounded-lg border font-medium transition-colors ${meta.classes} ${
          compact ? "p-1.5" : "text-xs px-2.5 py-1.5"
        }`}
      >
        <Icon className="w-3.5 h-3.5" />
        {!compact && meta.label}
        <ChevronDown className={`w-3 h-3 opacity-60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 right-0 w-44 rounded-xl border border-border bg-surface shadow-lg py-1">
          {STATUS_ORDER.map((s) => {
            const m = STATUS_META[s];
            const ItemIcon = m.icon;
            const isCurrent = s === status;
            return (
              <button
                key={s}
                type="button"
                disabled={isCurrent}
                onClick={() => {
                  setOpen(false);
                  onSelect(s);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                  isCurrent
                    ? "text-foreground font-semibold cursor-default"
                    : "text-muted-foreground hover:text-foreground hover:bg-surface-muted"
                }`}
              >
                <ItemIcon className="w-3.5 h-3.5" /> {m.label}
                {isCurrent && <span className="ml-auto text-[10px] opacity-60">current</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
